<style lang="scss" scoped>
.page-wrap {
  padding: 8px 0;
  font-family: 'Segoe UI', sans-serif;
}

/* ── Full-screen loading overlay ── */
.full-load {
  position: fixed;
  inset: 0;
  background: rgba(13, 27, 62, 0.7);
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;

  .load-spinner {
    width: 48px;
    height: 48px;
    border: 4px solid rgba(255,255,255,0.2);
    border-top-color: #389ad4;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  .load-text {
    color: #fff;
    font-size: 15px;
    font-weight: 500;
  }
}

@keyframes spin { to { transform: rotate(360deg); } }

/* ── Panel ── */
.panel-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(13, 27, 62, 0.07);
  overflow: hidden;
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  background: #eef2fb;
  border-bottom: 1px solid #dde4f5;

  .panel-title {
    font-size: 13px;
    font-weight: 700;
    color: #389ad4;
    display: flex;
    align-items: center;
    gap: 8px;
  }
}

/* ── Toolbar ── */
.toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f3fa;

  .search-wrap {
    position: relative;
    flex: 1;
    max-width: 380px;

    .search-icon {
      position: absolute;
      left: 12px;
      top: 50%;
      transform: translateY(-50%);
      color: #a0aab8;
      font-size: 13px;
    }

    input {
      width: 100%;
      height: 40px;
      border: 1.5px solid #dde2ec;
      border-radius: 8px;
      padding: 0 12px 0 36px;
      font-size: 13px;
      color: #0d1b3e;
      outline: none;
      transition: border-color 0.2s, box-shadow 0.2s;

      &:focus {
        border-color: #389ad4;
        box-shadow: 0 0 0 3px rgba(56, 154, 212, 0.1);
      }

      &::placeholder { color: #b0bac9; }
    }
  }

  .btn-search {
    height: 40px;
    padding: 0 20px;
    background: #389ad4;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: opacity 0.2s;

    &:hover { opacity: 0.9; }
  }

  .legend {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 14px;
    font-size: 12px;
    color: #7a8499;

    .legend-dot {
      width: 10px;
      height: 10px;
      border-radius: 3px;
      display: inline-block;
      margin-right: 4px;

      &.dot-green { background: #c6f6d5; border: 1px solid #68d391; }
      &.dot-red   { background: #fed7d7; border: 1px solid #fc8181; }
    }
  }
}

/* ── Table ── */
.dl-table {
  width: 100%;
  font-size: 12.5px;
  border-collapse: collapse;
  table-layout: fixed;

  thead tr th {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: #7a8499;
    padding: 10px 12px;
    border-bottom: 2px solid #eef0f8;
    background: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  tbody tr {
    border-bottom: 1px solid #f5f7fa;
    transition: background 0.15s;

    &:last-child { border-bottom: none; }

    &.row-deleted {
      background: #fff5f5;
      &:hover { background: #ffe8e8; }
    }

    &.row-transmitted {
      background: #f0fff4;
      &:hover { background: #e6ffed; }
    }

    &:not(.row-deleted):not(.row-transmitted):hover {
      background: #f8fbff;
    }

    td {
      padding: 10px 12px;
      color: #2d3748;
      vertical-align: middle;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

/* ── Status badges ── */
.badge-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;

  &.s-transmitted { background: #c6f6d5; color: #276749; }
  &.s-final       { background: #bee3f8; color: #2b6cb0; }
  &.s-ready       { background: #fefcbf; color: #7b6e1a; }
  &.s-draft       { background: #e2e8f0; color: #4a5568; }
}

.badge-ahca {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;

  &.ahca-ok    { color: #2b6cb0; }
  &.ahca-warn  { color: #c62828; }
  &.ahca-not   { color: #a0aab8; }
}

/* ── Download button ── */
.btn-download {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #eef2fb;
  color: #185FA5;
  border: 1px solid #c5d4f0;
  border-radius: 7px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 13px;

  &:hover {
    background: #389ad4;
    color: #fff;
    border-color: #389ad4;
  }
}

/* ── Empty / loading states ── */
.state-box {
  text-align: center;
  padding: 56px 24px;
  color: #a0aab8;
  font-size: 13px;

  i { font-size: 32px; display: block; margin-bottom: 10px; opacity: 0.5; }
  p { margin: 0; }
}
</style>

<style>
.swal2-popup .swal2-icon { margin-left: 39% !important; }
</style>

<template>
  <div class="page-wrap">

    <!-- Download overlay -->
    <div class="full-load" v-if="saveTCNFile">
      <div class="load-spinner"></div>
      <span class="load-text">Preparing file, please wait…</span>
    </div>

    <div class="panel-card">

      <!-- Header -->
      <div class="panel-header">
        <span class="panel-title">
          <i class="fa fa-download"></i>
          Download TCN
        </span>
        <span style="font-size:12px; color:#7a8499;" v-if="enrollments.length > 0">
          {{ enrollments.length }} record{{ enrollments.length !== 1 ? 's' : '' }}
        </span>
      </div>

      <!-- Toolbar -->
      <div class="toolbar">
        <div class="search-wrap">
          <i class="fa fa-search search-icon"></i>
          <input
            v-model="search_text"
            type="text"
            placeholder="Search by enrollment no. or TCN…"
            @keypress.enter="search()"
          />
        </div>
        <button class="btn-search" @click="search()">
          <i class="fa fa-search"></i> Search
        </button>
        <div class="legend" v-if="enrollments.length > 0">
          <span><span class="legend-dot dot-green"></span>Transmitted</span>
          <span><span class="legend-dot dot-red"></span>Deleted</span>
        </div>
      </div>

      <!-- Loading -->
      <div class="state-box" v-if="getLoadingData">
        <v-data-loader></v-data-loader>
      </div>

      <!-- Empty state (initial) -->
      <div class="state-box" v-else-if="!getLoadingData && enrollments.length === 0 && !searched">
        <i class="fa fa-search"></i>
        <p>Search by enrollment number or TCN to view records</p>
      </div>

      <!-- No results -->
      <div class="state-box" v-else-if="!getLoadingData && enrollments.length === 0 && searched">
        <i class="fa fa-inbox"></i>
        <p>No enrollments found for "{{ last_search }}"</p>
      </div>

      <!-- Table -->
      <div v-else style="overflow-x: auto;">
        <table class="dl-table">
          <thead>
            <tr>
              <th style="width:8%">Date</th>
              <th style="width:5%">Enr No.</th>
              <th style="width:5%">App No.</th>
              <th style="width:12%">Applicant</th>
              <th style="width:10%">Service</th>
              <th style="width:10%">Location</th>
              <th style="width:9%">TCN</th>
              <th style="width:5%; text-align:center">Prints</th>
              <th style="width:5%; text-align:center">Resubmits</th>
              <th style="width:8%; text-align:center">AHCA</th>
              <th style="width:8%">Status</th>
              <th style="width:9%">Transmitted By</th>
              <th style="width:6%; text-align:center">File</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in enrollments"
              :key="index"
              :class="[
                item.is_deleted ? 'row-deleted' : '',
                !item.is_deleted && isTransmitted(item) ? 'row-transmitted' : ''
              ]"
            >
              <td>{{ item.enroll_date || dateFormat(item.created_at) }}</td>
              <td>{{ item.id }}</td>
              <td>{{ item.appointment_id }}</td>
              <td>{{ item.applicant || (item.fname + ' ' + item.mname + ' ' + item.lname) }}</td>
              <td>{{ item.service_name }}</td>
              <td>{{ item.originating_location_name }}</td>
              <td style="font-family:monospace; font-size:11px;">{{ item.tcn || '—' }}</td>
              <td style="text-align:center">{{ item.print_count }}</td>
              <td style="text-align:center">{{ item.resubmit_count }}</td>

              <!-- AHCA Status -->
              <td style="text-align:center">
                <template v-if="item.service_type === 'fdle'">
                  <span v-if="item.acha_submit_status === '300' && item.acha_response && item.acha_response.IsSuccessful === 'true'" class="badge-ahca ahca-ok">
                    <i class="fa fa-check-circle"></i> OK
                  </span>
                  <span v-else-if="item.acha_submit_status === '300' && item.acha_response && item.acha_response.IsSuccessful === 'false'" class="badge-ahca ahca-warn">
                    <i class="fa fa-exclamation-circle"></i> Error
                  </span>
                  <span v-else-if="item.acha_submit_status === '300'" class="badge-ahca ahca-ok">
                    <i class="fa fa-check"></i> Submitted
                  </span>
                  <span v-else class="badge-ahca ahca-not">—</span>
                </template>
                <span v-else class="badge-ahca ahca-not">—</span>
              </td>

              <!-- Transmit Status -->
              <td>
                <span v-if="(item.transmit_status === '300' || item.transmit_status === '600') && item.service_type !== 'fingerprintcard'" class="badge-status s-transmitted">
                  <i class="fa fa-check"></i> Transmitted
                </span>
                <span v-else-if="(item.transmit_status === '300' || item.transmit_status === '600') && item.service_type === 'fingerprintcard'" class="badge-status s-final">
                  Final
                </span>
                <span v-else-if="(item.transmit_status === '200' || item.transmit_status === '500') && item.service_type === 'fingerprintcard'" class="badge-status s-final">
                  Final
                </span>
                <span v-else-if="item.transmit_status === '200' || item.transmit_status === '500'" class="badge-status s-ready">
                  Ready
                </span>
                <span v-else class="badge-status s-draft">Draft</span>
              </td>

              <!-- Transmitted by -->
              <td style="font-size:12px; color:#4a5568;">
                <template v-if="(item.transmit_status === '300' || item.transmit_status === '600') && item.service_type !== 'fingerprintcard'">
                  {{ (item.transmitted_by == null || item.transmitted_by === '') ? item.updated_by_user : item.transmitted_by }}
                </template>
              </td>

              <!-- Download -->
              <td style="text-align:center">
                <button class="btn-download" @click="downloadFile(item.tcn, item.id)" title="Download .eft file">
                  <i class="fa fa-download"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

    </div>
  </div>
</template>

<script>
import moment from "moment";
import {
  FETCH_ADMIN_ENROLLMENT,
  DOWNLOAD_ENROLLMENT_FILE
} from "@/core/services/store/actions.type";
import { SET_BREADCRUMB } from "@/core/services/store/mutations.type";
import { mapGetters } from "vuex";
import vDataLoader from "@/components/frontend/spinner.vue";

export default {
  components: { vDataLoader },
  computed: {
    ...mapGetters(["currentUser", "getClients", "getServices"])
  },
  filters: {
    toFixedTwoDigit(amount) {
      return parseFloat(amount).toFixed(2);
    }
  },
  data() {
    return {
      saveTCNFile: false,
      getLoadingData: false,
      enrollments: [],
      search_text: "",
      searched: false,
      last_search: "",
      loadingMessage: "",
      breadCrumbs: [
        { id: 1, title: "Dashboard", route: "client.dashboard" },
        { id: 2, title: "Download TCN", route: "" }
      ]
    };
  },
  mounted() {
    this.$store.commit(SET_BREADCRUMB, this.breadCrumbs);
  },
  methods: {
    dateFormat(date) {
      if (!date) return "";
      return moment(date).format("MM/DD/YYYY");
    },
    isTransmitted(item) {
      return (
        item.transmit_status === "600" ||
        item.transmit_status === "300" ||
        ((item.transmit_status === "200" || item.transmit_status === "500") &&
          item.service_type === "fingerprintcard")
      );
    },
    search() {
      this.enrollments = [];
      this.getLoadingData = true;
      this.last_search = this.search_text.trim();
      this.$store
        .dispatch(FETCH_ADMIN_ENROLLMENT, { search_text: this.last_search })
        .then(res => {
          this.getLoadingData = false;
          this.searched = true;
          this.enrollments = res.data;
        })
        .catch(err => {
          this.getLoadingData = false;
          this.searched = true;
          this.$toastr.e(err.error || "Search failed");
        });
    },
    downloadFile(tcn, id) {
      this.saveTCNFile = true;
      this.$store
        .dispatch(DOWNLOAD_ENROLLMENT_FILE, { tcn, id })
        .then(response => {
          const b64toBlob = (b64Data, contentType = "", sliceSize = 512) => {
            const byteCharacters = atob(b64Data);
            const byteArrays = [];
            for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
              const slice = byteCharacters.slice(offset, offset + sliceSize);
              const byteNumbers = new Array(slice.length);
              for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
              }
              byteArrays.push(new Uint8Array(byteNumbers));
            }
            return new Blob(byteArrays, { type: contentType });
          };

          const blob = b64toBlob(response.data, "");
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", tcn + ".eft");
          document.body.appendChild(link);
          link.click();
          URL.revokeObjectURL(link.href);
          document.body.removeChild(link);
          this.saveTCNFile = false;
        })
        .catch(err => {
          this.$toastr.e(err.data.msg);
          this.saveTCNFile = false;
        });
    }
  }
};
</script>
